import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  firebase,
  getToken,
  rtdb,
  sendPushNotification,
} from "../../../utils/firebase";
import { BASE_URL, environment } from "utils/constant";
import SnagList from "../../Project Dashboard/Snags/components/SnagList";
import ChatHeader from "../../Project Dashboard/Snags/components/ChatHeader";
import MessageBubble from "../../Project Dashboard/Snags/components/MessageBubble";
import MessageInput from "../../Project Dashboard/Snags/components/MessageInput";
import ImageModal from "../../Project Dashboard/Snags/components/ImageModal";
import NoMessage from "assets/empty state/NoMessage.svg";

const GlobalSnagsChat = ({ id: propId }) => {
  const { snagRoomId } = useParams();
  const id = snagRoomId || propId;
  const [snags, setSnags] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newSnagTitle, setNewSnagTitle] = useState("");
  const [showNewSnagForm, setShowNewSnagForm] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const [showChatrooms, setShowChatrooms] = useState(true);
  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [activeSnag, setActiveSnag] = useState(null);
  const [activeGlobalChat, setActiveGlobalChat] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [lastMessages, setLastMessages] = useState({});
  const [generalMessages, setGeneralMessages] = useState([]);
  const [trigger, setTrigger] = useState(0);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [organisationId, setOrganisationId] = useState(
    JSON.parse(localStorage.getItem("organization"))?.id
  );
  const [snagMembers, setSnagMembers] = useState({}); // { snagId: [userId,…] }
  const [showMemberModal, setShowMemberModal] = useState(null); // snagId or null
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [initialMemberIds, setInitialMemberIds] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();

  //   Push an in-app notification to RTDB so it shows in Notifications modal
  const pushInAppNotification = async ({ title, message, snagId, target_ids = [] }) => {
    try {
      const organization = JSON.parse(
        localStorage.getItem("organization") || "{}"
      );
      const organizationId = organization?.id;
      if (!organizationId) return;

      await rtdb.ref(`notifications-${environment}/${organizationId}`).push({
        title,
        message,
        date: new Date().toISOString(),
        read: false,
        type: "snag_message",
        metadata: {
          snag_id: snagId,
          type: "new_message"
        },
        target_ids,
        module: "Chats",
        actor_id: currentUser.user,
      });
    } catch (error) {
      console.error("Failed to push in-app notification:", error);
    }
  };

  useEffect(() => {
    let organisation = JSON.parse(localStorage.getItem("organization"));
    if (organisation) {
      setOrganisationId(organisation?.id);
    }
  }, [localStorage.getItem("organization")]);

  const markMessagesAsRead = async (chatId, isGeneral = false) => {
    if (!currentUser?.user) return;

    const readPath = isGeneral
      ? `global_snags-${environment}/${organisationId}/general_updates/read_by/${currentUser.user}`
      : `global_snags-${environment}/${organisationId}/${chatId}/read_by/${currentUser.user}`;

    try {
      await rtdb.ref(readPath).set({
        lastReadAt: firebase.database.ServerValue.TIMESTAMP,
        userId: currentUser.user,
      });
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  };

  // Function to calculate unread counts
  const calculateUnreadCount = (messages, readData) => {
    if (!readData || !currentUser?.user) return messages.length;

    const userReadData = readData[currentUser?.user];
    if (!userReadData || !userReadData.lastReadAt) return messages.length;

    return messages.filter(
      (msg) =>
        msg.createdAt > userReadData.lastReadAt &&
        msg.senderId !== currentUser.user
    ).length;
  };

  // Fetch team members
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const accessToken = localStorage.getItem("access");
        const response = await fetch(
          `${BASE_URL}/org/organization/${organisationId}/members/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          let members = data.data.map((member) => ({
            user_id: member.user.id,
            is_admin: member.is_admin,
            joined_as: member.joined_as,
            first_name: member.user.first_name,
            last_name: member.user.last_name,
            email: member.user.email,
          }));

          setTeamMembers(members);
        }
      } catch (error) {
        console.error("Error fetching team members:", error);
      }
    };

    if (organisationId) {
      fetchTeamMembers();
    }
  }, [organisationId]);

  // Fetch snags list
  useEffect(() => {
    if (!organisationId) return;

    const snagsRef = rtdb.ref(`global_snags-${environment}/${organisationId}`);
    const handler = (snapshot) => {
      const snagsData = [];
      const membersMap = {};

      snapshot.forEach((child) => {
        const key = child.key;
        if (key !== "general_updates") {
          const val = child.val();
          snagsData.push({ id: key, ...val });

          // keep members map for quick lookup
          if (Array.isArray(val.members)) {
            membersMap[key] = val.members;
          } else {
            membersMap[key] = []; // treat as “open to all”
          }
        }
      });

      setSnags(snagsData.sort((a, b) => b.createdAt - a.createdAt));
      setSnagMembers(membersMap);

      // auto-select first snag if nothing selected and no ID in URL
      if (!activeSnag && !activeGlobalChat && !id && snagsData.length) {
        navigate(`/dashboard/global-communication/${snagsData[0].id}`);
      }
    };

    snagsRef.on("value", handler);
    return () => snagsRef.off("value", handler);
  }, [activeSnag, activeGlobalChat, organisationId]);

  // Merged useEffect for messages, unread counts, and last messages
  useEffect(() => {
    if (!organisationId || !currentUser?.user) return;

    const unsubscribeFunctions = [];
    const unreadCountsObj = {};
    const lastMessagesObj = {};

    // Handle General Updates
    const generalMessagesRef = rtdb.ref(
      `global_snags-${environment}/${organisationId}/general_updates/messages`
    );
    const generalReadRef = rtdb.ref(
      `global_snags-${environment}/${organisationId}/general_updates/read_by`
    );

    const handleGeneralMessages = async (snapshot) => {
      const msgs = [];

      snapshot.forEach((childSnapshot) => {
        const msgData = childSnapshot.val();
        msgs.push({
          id: childSnapshot.key,
          ...msgData,
        });
      });

      setGeneralMessages(msgs);

      // Update last message for general updates
      if (msgs.length > 0) {
        const lastMsg = msgs[msgs.length - 1];
        lastMessagesObj["general-updates"] = {
          text: lastMsg.text || (lastMsg.has_media ? "Media" : ""),
          senderName: lastMsg.senderName,
          createdAt: lastMsg.createdAt,
        };
      }

      // Handle read status for general updates
      generalReadRef.on("value", (readSnapshot) => {
        const readData = readSnapshot.val() || {};
        unreadCountsObj["general-updates"] = calculateUnreadCount(
          msgs,
          readData
        );

        setUnreadCounts({ ...unreadCountsObj });
        setLastMessages({ ...lastMessagesObj });
      });
    };

    generalMessagesRef
      .orderByChild("createdAt")
      .on("value", handleGeneralMessages);
    unsubscribeFunctions.push(() => {
      generalMessagesRef.off();
      generalReadRef.off();
    });

    // Handle individual snags
    const handleSnagMessages = (snag) => {
      const snagMessagesRef = rtdb.ref(
        `global_snags-${environment}/${organisationId}/${snag.id}/messages`
      );
      const snagReadRef = rtdb.ref(
        `global_snags-${environment}/${organisationId}/${snag.id}/read_by`
      );

      const handleMessages = async (snapshot) => {
        const msgs = [];

        snapshot.forEach((childSnapshot) => {
          const msgData = childSnapshot.val();
          msgs.push({
            id: childSnapshot.key,
            ...msgData,
          });
        });

        // Sort messages by createdAt
        msgs.sort((a, b) => a.createdAt - b.createdAt);

        // Update messages for active snag
        if (snag.id === activeSnag) {
          setMessages(msgs);
        }

        // Update last message for this snag
        if (msgs.length > 0) {
          const lastMsg = msgs[msgs.length - 1];
          lastMessagesObj[snag.id] = {
            text: lastMsg.text || (lastMsg.has_media ? "Media" : ""),
            senderName: lastMsg.senderName,
            createdAt: lastMsg.createdAt,
          };
        }

        // Handle read status
        snagReadRef.on("value", (readSnapshot) => {
          const readData = readSnapshot.val() || {};
          unreadCountsObj[snag.id] = calculateUnreadCount(msgs, readData);

          setUnreadCounts({ ...unreadCountsObj });
          setLastMessages({ ...lastMessagesObj });
        });
      };

      snagMessagesRef.orderByChild("createdAt").on("value", handleMessages);

      unsubscribeFunctions.push(() => {
        snagMessagesRef.off();
        snagReadRef.off();
      });
    };

    // Subscribe to all existing snags
    snags.forEach(handleSnagMessages);

    // Cleanup function
    return () => {
      unsubscribeFunctions.forEach((cleanup) => cleanup());
    };
  }, [currentUser?.user, snags, activeSnag, trigger, organisationId]);

  // Auto-scroll to bottom
  useEffect(() => {
    scrollToBottom();
  }, [messages, generalMessages]);

  // Mark messages as read when switching chats
  useEffect(() => {
    if (!window.location.pathname.includes("/dashboard/global-communication")) return;
    if (!id) {
      navigate("/dashboard/global-communication/general-updates", { replace: true });
      return;
    }

    if (id === "general-updates") {
      setActiveGlobalChat("general-updates");
      setActiveSnag(null);
      markMessagesAsRead("general-updates", true);
    } else if (id) {
      setActiveSnag(id);
      setActiveGlobalChat(null);
      markMessagesAsRead(id, false);
    }
  }, [id, currentUser?.user, organisationId, navigate]);

  const handleRoomSelect = (roomId, isGeneral = false) => {
    if (isGeneral) {
      navigate("/dashboard/global-communication/general-updates");
    } else {
      navigate(`/dashboard/global-communication/${roomId}`);
    }
    setTrigger((prev) => prev + 1);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleCreateSnag = async () => {
    if (!newSnagTitle.trim()) return;

    const newSnag = {
      title: newSnagTitle,
      createdAt: firebase.database.ServerValue.TIMESTAMP,
      createdBy: currentUser?.user || "unknown",
      creatorName:
        `${currentUser?.first_name} ${currentUser?.last_name}` || "Anonymous",
      messages: {},
    };

    try {
      const snagsRef = rtdb.ref(
        `global_snags-${environment}/${organisationId}`
      );
      const newSnagRef = await snagsRef.push(newSnag);
      setActiveSnag(newSnagRef.key);
      setNewSnagTitle("");
      setShowNewSnagForm(false);
    } catch (error) {
      console.error("Error creating snag:", error);
    }
  };

  const isOwnerOrAdmin = teamMembers.some(
    (m) =>
      m.user_id === currentUser?.user && (m.joined_as === "owner" || m.is_admin)
  );

  const visibleSnags = snags.filter((snag) => {
    if (isOwnerOrAdmin) return true; // see everything
    const members = snagMembers[snag.id] ?? [];
    return members.length === 0 || members.includes(currentUser?.user);
  });

  const saveSnagMembers = async (snagId) => {
    await rtdb
      .ref(`global_snags-${environment}/${organisationId}/${snagId}/members`)
      .set(selectedMemberIds.length ? selectedMemberIds : null); // null = open to all
    setShowMemberModal(null);
    setSelectedMemberIds([]);
  };

  const deleteSnag = async (snagId, title) => {
    if (!isOwnerOrAdmin) return;
    try {
      await rtdb
        .ref(`global_snags-${environment}/${organisationId}/${snagId}`)
        .remove();
      if (activeSnag === snagId) {
        setActiveSnag(null);
        setMessages([]);
      }
    } catch (error) {
      console.error("Error deleting snag:", error);
    }
  };

  const sendSnagMessage = async (text) => {
    if (!text.trim() && mediaFiles.length === 0) return;

    /* ---------- upload media ---------- */
    let mediaArray = [];
    let firstImage = null;
    if (mediaFiles.length) {
      setIsUploading(true);
      const accessToken = localStorage.getItem("access");
      mediaArray = await Promise.all(
        mediaFiles.map(async (file) => {
          const fd = new FormData();
          fd.append("site_id", organisationId);
          fd.append(
            "snag_id",
            activeGlobalChat === "general-updates"
              ? "general-updates"
              : activeSnag
          );

          let type = "document";
          if (file.type.startsWith("image/")) type = "image";
          else if (file.type.startsWith("video/")) type = "video";
          else if (file.type.startsWith("audio/")) type = "audio";

          fd.append("type", type);

          if (type === "image") {
            fd.append("image", file);
          } else {
            fd.append("file", file);
          }

          const r = await fetch(`${BASE_URL}/core/uploadsnagobjects/`, {
            method: "POST",
            headers: { Authorization: `Bearer ${accessToken}` },
            body: fd,
          });
          const j = await r.json();
          if (type === "image" && !firstImage) {
            firstImage = j.data.image;
          }
          return {
            url: j.data.image || j.data.file,
            type: type,
            mediaId: j.data.id,
          };
        })
      );
      setIsUploading(false);
      setMediaFiles([]);
    }

    /* ---------- build message object ---------- */
    const msg = {
      text: text,
      createdAt: firebase.database.ServerValue.TIMESTAMP,
      senderId: currentUser?.user,
      senderName:
        `${currentUser?.first_name} ${currentUser?.last_name}` || "Anonymous",
      media: mediaArray.length ? mediaArray : null,
      has_media: mediaArray.length > 0,
      replyTo: replyToMessage
        ? replyToMessage
        : null,
    };

    /* ---------- push message ---------- */
    const isGeneral = activeGlobalChat === "general-updates";
    const snag_name = isGeneral
      ? "general_updates"
      : snags.find((s) => s.id === activeSnag)?.title || activeSnag;

    const msgRef = isGeneral
      ? rtdb.ref(
        `global_snags-${environment}/${organisationId}/general_updates/messages`
      )
      : rtdb.ref(
        `global_snags-${environment}/${organisationId}/${activeSnag}/messages`
      );

    await msgRef.push(msg);
    setReplyToMessage(null);
    setTrigger((p) => p + 1);
    setTimeout(scrollToBottom, 100);

    /* ---------- NOTIFICATIONS ---------- */
    const membersForNotif = isGeneral
      ? teamMembers.map((m) => m.user_id) // general → all
      : snagMembers[activeSnag] && snagMembers[activeSnag].length
        ? snagMembers[activeSnag] // private snag
        : teamMembers.map((m) => m.user_id); // open snag → all

    const notif = {
      title: `${snag_name} - Global`,
      body: `${currentUser?.first_name} ${currentUser?.last_name}: ${text}${mediaArray.length ? " (media)" : ""
        }`,
      data: {
        snag_id: isGeneral ? "general_updates" : activeSnag,
        url: `sitemaster://chat-room?roomId=global:${organisationId}:${isGeneral ? "general_updates" : activeSnag}&roomName=${snag_name}`
      },
    };

    // send push to each member
    for (const uid of membersForNotif) {
      const token = await getToken(uid);
      if (token?.token && uid !== currentUser?.user)
        sendPushNotification(token.token, notif.title, notif.body, notif.data, firstImage ? true : false, firstImage);
    }

    // in-app notification
    await pushInAppNotification({
      title: notif.title,
      message: notif.body,
      snagId: isGeneral ? "general-updates" : activeSnag,
      target_ids: membersForNotif,
    });
  };

  const handleDeleteMessage = async (msgId) => {
    const chatPath =
      activeGlobalChat === "general-updates"
        ? `global_snags-${environment}/${organisationId}/general_updates/messages/${msgId}`
        : `global_snags-${environment}/${organisationId}/${activeSnag}/messages/${msgId}`;
    await rtdb.ref(chatPath).remove();
  };

  const handleUpdateMessage = async (msgId, newText, replyTo) => {
    const chatPath =
      activeGlobalChat === "general-updates"
        ? `global_snags-${environment}/${organisationId}/general_updates/messages/${msgId}`
        : `global_snags-${environment}/${organisationId}/${activeSnag}/messages/${msgId}`;
    await rtdb.ref(chatPath).update({
      text: newText,
      replyTo: replyTo || null,
    });
    setTrigger((prev) => prev + 1);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const newMediaFiles = Array.from(files).filter(
        (file) =>
          file.type.startsWith("image/") || file.type.startsWith("video/")
      );

      if (newMediaFiles.length > 0) {
        setMediaFiles((prev) => [...prev, ...newMediaFiles]);
      }
    }
  };

  const currentSnag = snags.find((snag) => snag.id === activeSnag);
  const activeMessages =
    activeGlobalChat === "general-updates" ? generalMessages : messages;

  return (
    <div className="flex h-[92vh] w-[85%] bg-gray-50 border-l-[1px] border-[#E5E7EB] overflow-hidden">
      <SnagList
        activeGlobalChat={activeGlobalChat}
        setActiveGlobalChat={(val) => handleRoomSelect(val, true)}
        setActiveSnag={(val) => handleRoomSelect(val, false)}
        setTrigger={setTrigger}
        markMessagesAsRead={markMessagesAsRead}
        lastMessages={lastMessages}
        unreadCounts={unreadCounts}
        showChatrooms={showChatrooms}
        setShowChatrooms={setShowChatrooms}
        showNewSnagForm={showNewSnagForm}
        setShowNewSnagForm={setShowNewSnagForm}
        isOwnerOrAdmin={isOwnerOrAdmin}
        newSnagTitle={newSnagTitle}
        setNewSnagTitle={setNewSnagTitle}
        handleCreateSnag={handleCreateSnag}
        visibleSnags={visibleSnags}
        activeSnag={activeSnag}
        deleteSnag={deleteSnag}
      />

      <div className="flex-1 flex flex-col">
        <ChatHeader
          activeGlobalChat={activeGlobalChat}
          currentSnag={currentSnag}
          isOwnerOrAdmin={isOwnerOrAdmin}
          setSelectedMemberIds={setSelectedMemberIds}
          setInitialMemberIds={setInitialMemberIds}
          snagMembers={snagMembers}
          setShowMemberModal={setShowMemberModal}
        />

        {/* Messages Area */}
        {(currentSnag || activeGlobalChat === "general-updates") ? (
          <div
            className={`flex-1 overflow-y-auto p-4 bg-white text-blue-900 relative ${isDragging ? "border-4 border-dashed border-purple-500" : ""
              }`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {isDragging && (
              <div className="absolute inset-0 bg-purple-100 bg-opacity-90 flex items-center justify-center z-10 pointer-events-none">
                <div className="text-center">
                  <div className="text-6xl mb-4">📁</div>
                  <div className="text-xl font-bold text-purple-900">
                    Drop media files here
                  </div>
                  <div className="text-sm text-purple-700 mt-2">
                    Images and videos accepted
                  </div>
                </div>
              </div>
            )}

            {activeMessages.length === 0 ? (
              <div className="flex flex-col items-center h-full justify-center text-gray-500">
                <img src={NoMessage} alt="No Messages" className="h-44 w-44" />
                <div>No messages yet. Start the conversation!</div>
              </div>
            ) : (
              activeMessages.map((msg, index) => (
                <MessageBubble
                  key={msg.id}
                  msg={msg}
                  previousMessage={index > 0 ? activeMessages[index - 1] : null}
                  currentUser={currentUser}
                  menuOpenFor={menuOpenFor}
                  setMenuOpenFor={setMenuOpenFor}
                  onDelete={handleDeleteMessage}
                  onUpdate={handleUpdateMessage}
                  setReplyToMessage={setReplyToMessage}
                  setShowImagePreview={setShowImagePreview}
                  setSelectedMedia={setSelectedMedia}
                  teamMembers={teamMembers}
                />
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-white">
            <div className="text-gray-500">Select a chatroom to start messaging</div>
          </div>
        )}

        {/* Message Input */}
        {(currentSnag || activeGlobalChat === "general-updates") && (
          <MessageInput
            onSend={sendSnagMessage}
            replyToMessage={replyToMessage}
            setReplyToMessage={setReplyToMessage}
            teamMembers={teamMembers}
            isUploading={isUploading}
            mediaFiles={mediaFiles}
            setMediaFiles={setMediaFiles}
          />
        )}
      </div>

      {showImagePreview && (
        <ImageModal
          imageUrl={selectedMedia}
          onClose={() => setShowImagePreview(false)}
        />
      )}

      {showMemberModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-5 w-96 max-h-[80vh] overflow-y-auto">
            <h3 className="font-bold mb-3">Select members for this snag</h3>
            {/* Preview and quick actions */}
            <div className="mb-3">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>
                  Selected: <span className="font-medium">{selectedMemberIds.length}</span>
                </span>
                <div className="flex items-center gap-2">
                  <button
                    className="underline hover:text-purple-700"
                    onClick={() => setSelectedMemberIds(teamMembers.map((m) => m.user_id))}
                  >
                    Select all
                  </button>
                  <button
                    className="underline hover:text-purple-700"
                    onClick={() => setSelectedMemberIds([])}
                  >
                    Clear
                  </button>
                </div>
              </div>
              {selectedMemberIds.filter((id) => !initialMemberIds.includes(id)).length > 0 && (
                <div className="mt-2">
                  <div className="text-xs text-gray-500 mb-1">Will add:</div>
                  <div className="flex flex-wrap gap-2">
                    {selectedMemberIds
                      .filter((id) => !initialMemberIds.includes(id))
                      .map((id) => {
                        const m = teamMembers.find((tm) => tm.user_id === id);
                        if (!m) return null;
                        return (
                          <span
                            key={id}
                            className="px-2 py-0.5 text-xs bg-green-50 text-green-800 border border-green-200 rounded-full"
                          >
                            {m.first_name} {m.last_name}
                          </span>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-1">
              {teamMembers.map((m) => {
                const checked = selectedMemberIds.includes(m.user_id);
                return (
                  <label
                    key={m.user_id}
                    className="flex items-center gap-2 cursor-pointer select-none"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        setSelectedMemberIds((prev) =>
                          checked
                            ? prev.filter((id) => id !== m.user_id)
                            : [...prev, m.user_id]
                        );
                      }}
                      className="rounded"
                    />
                    <span>
                      {m.first_name} {m.last_name}
                      {m.joined_as === "owner" && " (owner)"}
                      {m.is_admin &&
                        !m.joined_as?.includes("owner") &&
                        " (admin)"}
                    </span>
                  </label>
                );
              })}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowMemberModal(null)}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={() => saveSnagMembers(showMemberModal)}
                className="px-3 py-1 text-sm bg-purple-700 text-white rounded hover:bg-purple-800"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSnagsChat;